import React, { ComponentProps, FC } from "react";
import { DriverAvatar } from "@src/common/components/Avatar";
import { DriverMapItem } from "@src/common/components/Widgets/DriverMapWidget";
import classNames from "classnames";
import { ShipmentRow } from "../types";
import { ShipmentStatus } from "@api/graphql/generated/generated-types";
import { XCircleIcon } from "@heroicons/react/24/outline";

interface ShipmentStatusCellProps {
  status?: string;
  StatusIcon?: FC<ComponentProps<"svg">>;
  driverPhoto?: string;
  driverName?: string;
  avatarDisplay?: string | undefined;
  driver?: DriverMapItem;
  isCompleted?: boolean;
  row?: ShipmentRow;
  vehicleShortCode?: string;
}

export function ShipmentStatusCell({
  status,
  StatusIcon,
  driverPhoto,
  driverName,
  avatarDisplay,
  driver,
  isCompleted,
  row,
  vehicleShortCode,
}: ShipmentStatusCellProps) {
  const iconClassNames = classNames("h-5 w-5", {
    "text-green-400": isCompleted,
  });

  const renderDriverOrStatusIcon = () => {
    if (status !== "Unassigned" && !row?.shipment?.routingJobId && driverName) {
      return (
        <DriverAvatar
          driverPhoto={driverPhoto}
          driverName={driverName}
          avatarDisplay={avatarDisplay || undefined}
          size={28}
          isActive={driver?.isActive}
        />
      );
    }

    if (StatusIcon) {
      return <StatusIcon className={iconClassNames} />;
    }

    return null;
  };

  const renderReviewWaitTimeLabel = () => {
    const shipment = row?.shipment;
    if (!shipment) {
      return null;
    }

    const isDelivered = shipment.status === ShipmentStatus.Completed;
    const isClosed = shipment.isClosed;
    const driverReportedWaitTimeInMinutes =
      shipment.driverReportedWaitTimeInMinutes;
    const waitTimeUnfreeMinutesOverride =
      shipment.waitTimeUnfreeMinutesOverride;
    const waitTimeInMinutes = shipment.waitTimeInMinutes;

    const showLabel =
      isDelivered &&
      !isClosed &&
      !waitTimeUnfreeMinutesOverride &&
      ((driverReportedWaitTimeInMinutes ?? 0) > 0 ||
        (waitTimeUnfreeMinutesOverride ?? 0) > 0 ||
        (waitTimeInMinutes ?? 0) > 0);
    if (showLabel) {
      return <div className="text-xs text-red-600 ml-1">Review Wait Time</div>;
    }

    return null;
  };

  return (
    <div className="flex gap-2 items-center">
      <div className="flex flex-init flex-col gap-2" style={{ width: 110 }}>
        <div className="flex flex-init font-bold text-xs">
          <div className="flex items-center gap-1">
            <div style={{ width: 28 }} className="flex items-center gap-1">
              {renderDriverOrStatusIcon()}
            </div>
            <div className="flex flex-col">
              <div
                className={classNames({
                  "flex-1 ml-1": true,
                  "text-orange-400": status === "Unconfirmed" || status === "Attempted" || status === "Partial Completed",
                })}
              >
                {status}
              </div>
              {driverName && (
                <div
                  className="flex flex-1 text-xs ml-1 font-light truncate"
                  style={{ maxWidth: 100 }}
                >
                  {driverName} {vehicleShortCode}
                </div>
              )}
              {renderReviewWaitTimeLabel()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
